/**
 * The manual work-log form's "משך" field (owner 2026-10-11): a duration with
 * ready-made presets, the end time "now" by default and the start computed
 * from the duration. The start / end `datetime-local` values stay the source
 * of truth the form sends; these helpers only fill them. Pure, unit-tested.
 */

/** The preset buttons, in minutes: 15 דק׳, 30 דק׳, 1 שע׳, 2 שע׳. */
export const DURATION_PRESETS = [15, 30, 60, 120] as const;

/** One work log covers at most a day. */
export const MAX_DURATION_MINUTES = 24 * 60;

const pad = (n: number) => String(n).padStart(2, '0');

/** `datetime-local` value (local time, minute precision) for a Date. */
export function toLocalInputValue(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * What the user typed, in minutes, or null when it is not a duration:
 * - "1:30" is hours:minutes (90),
 * - a whole number is minutes ("45"),
 * - a decimal is hours ("1.5" or "1,5" = 90).
 * Accepted range: 1 minute to 24 hours.
 */
export function parseDurationInput(text: string): number | null {
  const s = text.trim().replace(',', '.');
  if (!s) return null;
  let minutes: number;
  const hm = /^(\d{1,2}):(\d{1,2})$/.exec(s);
  if (hm) {
    const m = Number(hm[2]);
    if (m > 59) return null;
    minutes = Number(hm[1]) * 60 + m;
  } else if (/^\d+$/.test(s)) {
    minutes = Number(s);
  } else if (/^\d*\.\d+$/.test(s)) {
    minutes = Math.round(Number(s) * 60);
  } else {
    return null;
  }
  return minutes >= 1 && minutes <= MAX_DURATION_MINUTES ? minutes : null;
}

/** Minutes as the field shows them: 90 → "1:30", 15 → "0:15". */
export function formatDurationInput(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  return `${Math.floor(m / 60)}:${pad(m % 60)}`;
}

/** The start that gives `minutes` before `endLocal` ('' when the end is not a date). */
export function startFromDuration(endLocal: string, minutes: number): string {
  const end = new Date(endLocal);
  if (!endLocal || Number.isNaN(end.getTime())) return '';
  return toLocalInputValue(new Date(end.getTime() - minutes * 60_000));
}

/** Whole minutes from start to end, or null when either is missing or the end is not later. */
export function minutesBetween(startLocal: string, endLocal: string): number | null {
  if (!startLocal || !endLocal) return null;
  const a = new Date(startLocal).getTime();
  const b = new Date(endLocal).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  const diff = Math.round((b - a) / 60_000);
  return diff > 0 ? diff : null;
}
